import { AlertTriangle, CheckCircle2, Info, OctagonAlert, X } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "./Button";

type NoticeTone = "info" | "warning" | "danger" | "success";

const toneClass: Record<NoticeTone, string> = {
  info: "border-info-border bg-info-bg text-info before:bg-info",
  warning: "border-warning-border bg-warning-bg text-warning before:bg-warning",
  danger: "border-danger-border bg-danger-bg text-danger before:bg-danger",
  success: "border-success-border bg-success-bg text-success before:bg-success",
};

const toneIcon = {
  info: Info,
  warning: AlertTriangle,
  danger: OctagonAlert,
  success: CheckCircle2,
};

export function Notice({
  title,
  children,
  tone = "info",
  dismissible = false,
  onDismiss,
  compact = false,
}: {
  title: string;
  children: ReactNode;
  tone?: NoticeTone;
  dismissible?: boolean;
  onDismiss?: () => void;
  compact?: boolean;
}) {
  const Icon = toneIcon[tone];

  return (
    <section
      className={`relative overflow-hidden rounded-card border ${compact ? "p-3 pl-4" : "p-4 pl-5"} shadow-[var(--shadow-card)] before:absolute before:inset-y-3 before:left-0 before:w-[3px] before:rounded-pill ${toneClass[tone]}`}
      aria-label={title}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-current">
          <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="page-eyebrow text-text">{title}</h2>
          <div
            className={`${compact ? "mt-1" : "mt-2"} text-sm leading-[var(--leading-body)] text-text-muted`}
          >
            {children}
          </div>
        </div>
        {dismissible ? (
          <Button aria-label="Dismiss notice" size="sm" variant="ghost" onClick={onDismiss}>
            <X aria-hidden="true" className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </section>
  );
}
